import React, { useState } from 'react';
import {
    FormControl,
    Select,
    MenuItem,
    Box,
    Typography,
    Chip,
    useTheme,
    alpha,
    TextField,
    Button
} from '@mui/material';
import {
    Today,
    History,
    DateRange,
    CalendarMonth,
    FilterList,
} from '@mui/icons-material';

const DateFilterComponent = ({ selectedFilter, onFilterChange, customStartDate, customEndDate, onCustomDateChange, onApplyCustom }) => {
    const theme = useTheme();
    const [startDate, setStartDate] = useState(customStartDate || '');
    const [endDate, setEndDate] = useState(customEndDate || '');

    const filterOptions = [
        {
            value: 'today',
            label: 'Today',
            icon: <Today fontSize="small" />,
            description: 'Show data for today only'
        },
        {
            value: 'yesterday',
            label: 'Yesterday',
            icon: <History fontSize="small" />,
            description: 'Show data for yesterday only'
        },
        {
            value: 'week',
            label: 'Past 7 Days',
            icon: <DateRange fontSize="small" />,
            description: 'Show data for the past week'
        },
        {
            value: 'month',
            label: 'Past 30 Days',
            icon: <CalendarMonth fontSize="small" />,
            description: 'Show data for the past month'
        },
        {
            value: 'custom',
            label: 'Custom',
            icon: <DateRange fontSize="small" />,
            description: 'Select a custom date range'
        }
    ];

    const getCurrentOption = () => {
        return filterOptions.find(option => option.value === selectedFilter) || filterOptions[2];
    };

    const handleCustomDateChange = (type, value) => {
        if (type === 'start') setStartDate(value);
        if (type === 'end') setEndDate(value);
        if (onCustomDateChange) onCustomDateChange(type, value);
    };

    const handleApply = () => {
        if (onApplyCustom) onApplyCustom(startDate, endDate);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
        >
            <Box display="flex" alignItems="center" gap={1}>
                <FilterList color="primary" />
                <Typography variant="subtitle2" fontWeight={600} color="primary">
                    Time Period:
                </Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                    value={selectedFilter}
                    onChange={(e) => onFilterChange(e.target.value)}
                    displayEmpty
                    sx={{
                        borderRadius: 2,
                        backgroundColor: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                        },
                    }}
                    renderValue={(selected) => {
                        const option = getCurrentOption();
                        return (
                            <Box display="flex" alignItems="center" gap={1}>
                                {option.icon}
                                <Typography variant="body2" fontWeight={500}>
                                    {option.label}
                                </Typography>
                            </Box>
                        );
                    }}
                >
                    {filterOptions.map((option) => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                },
                                '&.Mui-selected': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.16),
                                    },
                                },
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} width="100%">
                                <Box
                                    sx={{
                                        color: option.value === selectedFilter ? theme.palette.primary.main : 'inherit',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {option.icon}
                                </Box>
                                <Box flex={1}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={option.value === selectedFilter ? 600 : 400}
                                        color={option.value === selectedFilter ? 'primary' : 'inherit'}
                                    >
                                        {option.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.description}
                                    </Typography>
                                </Box>
                                {option.value === selectedFilter && (
                                    <Chip
                                        size="small"
                                        label="Active"
                                        color="primary"
                                        variant="filled"
                                        sx={{ fontSize: '0.65rem', height: 20 }}
                                    />
                                )}
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {selectedFilter === 'custom' && (
                <Box display="flex" alignItems="center" gap={2}>
                    <TextField
                        label="Start Date"
                        type="date"
                        size="small"
                        value={startDate}
                        onChange={(e) => handleCustomDateChange('start', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        size="small"
                        value={endDate}
                        onChange={(e) => handleCustomDateChange('end', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApply}
                        disabled={!startDate || !endDate}
                    >
                        Apply
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default DateFilterComponent;